/**
 * Chapter 4, the brand today (32) and its community (39). Template 32 is also
 * each competitor's snapshot (chapter 5): the same reads, per account.
 */
import type { ReactNode } from "react";
import type { AccountProfile, AccountScore, ContentClass, ContentMix, CriterionId, StrategyPost } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Glyph, Headline, Picture, Slide, SourceLine, V,
  compact, dayLong, dayShort, monthShortYear, num, rangeLong, slideText,
  type TemplateContext, NO_FIGURE
} from "./kit";

/* ── Shared parts ─────────────────────────────────────────────────────── */

/** The score panel on ink: the total, how much was read, the three parts. */
export function ScorePanel({ score, first }: { score: AccountScore; first: boolean }) {
  const parts: Array<[string, AccountScore["presence"]]> = [["Presence", score.presence], ["Content", score.content], ["Community", score.community]];
  const criteria = 21 - (score.dropped_criteria?.length ?? 0);
  return (
    <div className="sv-score">
      <p className="sv-score-title">{first ? "First score" : "Score"}</p>
      <V as="p" className="sv-score-figure">{score.total_shown != null ? Math.round(score.total_shown) : NO_FIGURE}</V>
      <p className="sv-score-of">out of 100{score.criteria_read != null ? <> · <V>{score.criteria_read}</V> of {criteria} criteria read</> : null}</p>
      <span className="sv-push" />
      {parts.map(([key, part]) => (
        <div key={key} className="sv-score-part">
          <p>{key}</p>
          <span className="sv-push" />
          {part ? <><V as="b">{Math.round(part.points)}</V><span> / {part.of}</span></> : <span>–</span>}
        </div>
      ))}
    </div>
  );
}

/** A post picture with its plays chip, and its line under it. */
export function PostCard({ ctx, post, label, className }: { ctx: TemplateContext; post: StrategyPost; label: ReactNode; className?: string }) {
  const plays = num(post.value);
  return (
    <div className={`sv-post${className ? ` ${className}` : ""}`}>
      <div className="sv-post-pic">
        <Picture ctx={ctx} label={label?.toString() ?? "A post"} url={post.picture_url} />
        {plays ? (
          <span className="sv-chip">
            <svg aria-hidden viewBox="0 0 13 13" width="13" height="13"><path d="M3.5 2.2v8.6L10.6 6.5z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>
            <V>{plays}</V> {post.kind === "picture" ? "reactions" : "plays"}
          </span>
        ) : null}
      </div>
      <p className="sv-post-label">{label}</p>
    </div>
  );
}

const MIX_COLOURS: Record<ContentClass, string> = { Brand: "#121212", Product: "#666666", Culture: "#a3a3a3", Promotion: "#d9d9d9" };
const MIX_ORDER: ContentClass[] = ["Brand", "Product", "Culture", "Promotion"];

/** One bar, four classes, with its legend. */
export function MixBar({ mix }: { mix: ContentMix }) {
  const share = (c: ContentClass) => mix.classes.find((x) => x.class === c)?.share_pct ?? 0;
  return (
    <>
      <div className="sv-mixbar">
        {MIX_ORDER.map((c) => (share(c) > 0 ? <i key={c} style={{ flexGrow: share(c), background: MIX_COLOURS[c] }} /> : null))}
      </div>
      <div className="sv-mixbar-legend">
        {MIX_ORDER.map((c) => (
          <span key={c}><i style={{ background: MIX_COLOURS[c] }} />{c} <V>{share(c)}%</V></span>
        ))}
      </div>
    </>
  );
}

const criterion = (score: AccountScore | null | undefined, id: CriterionId) => score?.criteria.find((c) => c.criterion_id === id);

/** The criterion's line, or what is missing. */
function CriterionItem({ title, line }: { title: string; line?: string | null }) {
  return (
    <div className="sv-item">
      <p className="sv-item-key">{title}</p>
      {line ? <V as="p" className="sv-item-value">{line}</V> : <p className="sv-item-value sv-open">Not measured yet</p>}
    </div>
  );
}

const postLabel = (post: StrategyPost) => [post.label ?? post.format ?? (post.platform ? post.platform : null), post.posted_at ? dayShort(post.posted_at) : null].filter(Boolean).join(" · ");

/** The foot's red flag: "Public read, 12 October · …". */
function ReadFlag({ readAt, extra }: { readAt?: string | null; extra?: ReactNode }) {
  if (!readAt && !extra) return null;
  return (
    <SourceLine className="sv-flag-line">
      {readAt ? <>Public read, {dayLong(readAt)}</> : null}{readAt && extra ? " · " : null}{extra}
    </SourceLine>
  );
}

/* ── 32 The brand today / a competitor's snapshot ─────────────────────── */

export function AccountSnapshotSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const t = slideText(ctx);
  const isBrand = ctx.slide.template.id === "brand-today";
  const competitor = ctx.slide.template.id === "snapshot" ? doc.competitors!.set[ctx.slide.template.competitor] : null;
  const profile: AccountProfile = isBrand ? doc.brand! : competitor!;
  const name = isBrand ? ctx.brand : competitor!.name;
  const score = profile.scorecard;
  const total = profile.accounts.reduce((n, a) => n + (a.followers?.value ?? 0), 0);
  const mix = profile.content_mix;
  const readAt = score?.read_at ?? profile.accounts.find((a) => a.followers?.as_of)?.followers?.as_of ?? null;
  const key = isBrand ? "brand" : competitor!.id;
  const leaders = doc.competitors?.set_summary?.part_leaders;
  const leads = leaders ? (["presence", "content", "community"] as const).filter((p) => leaders[p] === key) : [];
  const flagExtra = isBrand
    ? (ctx.doc.brand?.collaborations ? "community on the next slide" : null)
    : leads.length ? <>the set’s leader on <V>{leads.map((p) => p[0].toUpperCase() + p.slice(1)).join(" and ")}</V></> : null;
  return (
    <Slide>
      <div className="sv-col sv-fill sv-snap-copy">
        <Eyebrow ctx={ctx} extra={competitor ? <V>{competitor.name}</V> : undefined} />
        <Headline text={t.headline} fallback={`${name} today.`} valueWords={!t.headline} size={50} lineHeight={56} />
        <div className="sv-snap">
          <div className="sv-snap-presence">
            <div className="sv-head"><p>Presence</p></div>
            <div className="sv-snap-total">
              <V as="p">{compact(total)}</V>
              <p>followers across <V>{profile.accounts.length}</V> account{profile.accounts.length === 1 ? "" : "s"}</p>
            </div>
            {profile.accounts.map((a, i) => (
              <div key={i} className="sv-snap-account">
                <Glyph platform={a.platform} size={18} />
                <V as="b">{num(a.followers) ?? NO_FIGURE}</V>
                <V>{a.label ?? (a.platform === "YOUTUBE" ? "YouTube" : a.platform === "TIKTOK" ? "TikTok" : a.platform[0] + a.platform.slice(1).toLowerCase())}</V>
              </div>
            ))}
            <CriterionItem title="Where the audience is" line={criterion(score, "P1")?.evidence_line} />
            <CriterionItem title="One identity" line={criterion(score, "P2")?.evidence_line} />
            <CriterionItem title="Easy to find" line={criterion(score, "P4")?.evidence_line} />
            <CriterionItem title="The brand on show" line={criterion(score, "P5")?.evidence_line} />
          </div>
          <div className="sv-snap-content">
            <div className="sv-head"><p>Content</p></div>
            <div className="sv-snap-body">
              <div className="sv-snap-posts">
                {profile.best_posts.slice(0, 3).map((p, i) => (
                  <PostCard key={i} ctx={ctx} post={p} label={<V>{postLabel(p)}</V>} />
                ))}
              </div>
              <div className="sv-snap-info">
                {mix ? (
                  <div className="sv-item sv-item-mix">
                    <p className="sv-item-key sv-item-key-17">What they post</p>
                    <MixBar mix={mix} />
                    <p className="sv-item-value">
                      {mix.sample?.posts ? <V>{mix.sample.posts} posts{isBrand && mix.sample.window_start ? `, ${rangeLong(mix.sample.window_start, mix.sample.window_end)}` : ""}. </V> : null}
                      {mix.line ? <V>{mix.line}</V> : null}
                    </p>
                  </div>
                ) : null}
                <CriterionItem title="Made for each platform" line={criterion(score, "C1")?.evidence_line} />
                <CriterionItem title="Rhythm" line={criterion(score, "C4")?.evidence_line} />
                <CriterionItem title="Series" line={criterion(score, "C8")?.evidence_line} />
              </div>
            </div>
          </div>
        </div>
        <Foot ctx={ctx} label={competitor ? `${ctx.chapter?.foot ?? ""} · ${competitor.name}` : undefined} flag={<ReadFlag readAt={readAt} extra={flagExtra} />} />
      </div>
      {score ? <ScorePanel score={score} first={!score.previous} /> : <div className="sv-score"><p className="sv-score-title">Score</p><p className="sv-open">Not scored yet</p></div>}
    </Slide>
  );
}

/* ── 39 Community ─────────────────────────────────────────────────────── */

function Level({ level, setLevel }: { level?: number | null; setLevel?: number | null }) {
  if (level == null) return null;
  const whole = Math.round(level);
  return (
    <p className="sv-level">
      {Array.from({ length: 5 }, (_, i) => <i key={i} data-on={i < whole || undefined} />)}
      <span>Level <V>{whole}</V> of 5{setLevel != null ? <> · the set <V>{setLevel.toFixed(1)}</V></> : null}</span>
    </p>
  );
}


export function CommunitySlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const brand = doc.brand!;
  const t = slideText(ctx);
  const score = brand.scorecard;
  const m1 = criterion(score, "M1");
  const m3 = criterion(score, "M3");
  const setLevel = (id: CriterionId) => doc.competitors?.set_summary?.per_criterion.find((c) => c.criterion_id === id)?.set_average_level ?? null;
  const collab = brand.collaborations;
  const tagged = collab?.tagged_posts_per_month;
  return (
    <Slide>
      <div className="sv-col sv-fill sv-snap-copy">
        <Eyebrow ctx={ctx} extra="Community" />
        <Headline text={t.headline} fallback="How it talks *to people.*" size={50} lineHeight={56} />
        <div className="sv-snap">
          <div className="sv-comm-left">
            <div className="sv-head"><p>How it talks to people</p></div>
            <div className="sv-comm-measure">
              {m1?.figure?.value != null ? <V as="p" className="sv-comm-figure">{num(m1.figure)}</V> : <p className="sv-comm-figure">{NO_FIGURE}</p>}
              <p className="sv-comm-label">Of questions answered</p>
              {m1?.statement ? <V as="p" className="sv-comm-statement">{m1.statement}</V> : !m1 ? <p className="sv-comm-statement sv-open">Replies are not read yet.</p> : null}
              <Level level={m1?.level} setLevel={setLevel("M1")} />
            </div>
            <div className="sv-comm-measure sv-comm-ruled">
              {m3?.figure?.value != null ? <V as="p" className="sv-comm-figure">{num(m3.figure)}</V> : <p className="sv-comm-figure">{NO_FIGURE}</p>}
              <p className="sv-comm-label">Comments per 1,000 views</p>
              {m3?.statement ? <V as="p" className="sv-comm-statement">{m3.statement}</V> : null}
              <Level level={m3?.level} setLevel={setLevel("M3")} />
            </div>
            <div className="sv-item sv-comm-ruled">
              <p className="sv-item-key sv-item-key-17">People’s posts about the brand</p>
              {tagged?.value != null ? (
                <p className="sv-item-value sv-item-13">At least <V>{tagged.value}</V> tagged posts a month{collab?.tagged_line ? <>, <V>{collab.tagged_line.replace(/^\p{Lu}/u, (c) => c.toLowerCase())}</V></> : "."}</p>
              ) : <p className="sv-item-value sv-open">Tagged posts are not read yet.</p>}
            </div>
          </div>
          <div className="sv-comm-right">
            <div className="sv-head"><p>Campaigns and collaborations</p></div>
            <div className="sv-comm-body">
              <div className="sv-comm-lists">
                <div className="sv-comm-list">
                  <p className="sv-item-key sv-item-key-17">Creators, 90 days</p>
                  {(collab?.creators ?? []).slice(0, 4).map((c) => (
                    <div key={c.handle} className="sv-comm-row"><V as="b">@{c.handle.replace(/^@/, "")}</V><span className="sv-push" />{c.posts != null ? <span><V>{c.posts}</V> posts</span> : null}</div>
                  ))}
                  <p className="sv-item-value">
                    {collab?.creator_posts != null ? <><V>{collab.creator_posts}</V> posts with creators{collab.creator_posts_disclosed != null ? <>, <V>{collab.creator_posts_disclosed}</V> disclosed</> : null}. </> : null}
                    {collab?.creators_line ? <V>{collab.creators_line}</V> : null}
                  </p>
                </div>
                <div className="sv-comm-list sv-comm-campaigns">
                  <p className="sv-item-key sv-item-key-17">Campaigns, 12 months</p>
                  {(collab?.campaigns ?? []).slice(0, 4).map((c) => (
                    <div key={c.name} className="sv-comm-row"><V as="b">{c.name}</V><span className="sv-push" /><V>{monthShortYear(c.date)}</V></div>
                  ))}
                  {collab?.campaigns_line ? <V as="p" className="sv-item-value">{collab.campaigns_line}</V> : null}
                </div>
              </div>
              <div className="sv-comm-videos">
                {(collab?.campaign_posts ?? []).slice(0, 2).map((p, i) => (
                  <PostCard key={i} ctx={ctx} post={p} label={<V>{postLabel(p)}</V>} className="sv-post-120" />
                ))}
              </div>
            </div>
          </div>
        </div>
        <Foot ctx={ctx} label={`${ctx.chapter?.foot ?? ""} · Community`} flag={<ReadFlag readAt={score?.read_at} extra="comments of the last 20 posts" />} />
      </div>
      {score ? <ScorePanel score={score} first={!score.previous} /> : null}
    </Slide>
  );
}

